import { useMutation } from '@apollo/client/react';
import { type FC, useState } from 'react';

import InlineEditableField from '@zen/Components/InlineEditableField';
import LabelledValue from '@zen/Components/LabelledValue';
import ValueWithDefault from '@zen/Components/ValueWithDefault';
import { useNotification } from '@zen/utils/hooks/useNotification';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performMutation } from '@zen/utils/performMutation';

import {
  UpdateMarksAndNumbersDocument,
  type UpdateMarksAndNumbersMutationResult,
  type UpdateMarksAndNumbersMutationVariables
} from './graphql';

interface Props {
  canUpdateMarksAndNumbers: boolean;
  cargoId: string;
  marksAndNumbers: string;
}

const MarksAndNumbersInlineField: FC<Props> = ({ canUpdateMarksAndNumbers, cargoId, marksAndNumbers }) => {
  const [marksAndNumbersValue, setMarksAndNumbersValue] = useState<string>(marksAndNumbers);

  const { addSuccess, addError } = useNotification();

  const [updateMarksAndNumbers] = useMutation<UpdateMarksAndNumbersMutationResult, UpdateMarksAndNumbersMutationVariables>(
    UpdateMarksAndNumbersDocument
  );

  const label: string = 'Marks and numbers';

  const handleChange = (value: string): Promise<IOkOrErrorResult> => {
    if (!value || value.trim().length === 0) {
      addError(`${label} must not be blank`);
      throw Error();
    }

    return performMutation({
      mutationFn: () =>
        updateMarksAndNumbers({
          variables: {
            input: {
              cargoId,
              marksAndNumbers: value
            }
          }
        }),
      onError: () => addError(),
      onSuccess: () => {
        addSuccess(`${label} updated.`);
        setMarksAndNumbersValue(value);
      }
    });
  };

  if (!canUpdateMarksAndNumbers) {
    return (
      <LabelledValue label={label} variant="default">
        <ValueWithDefault value={marksAndNumbersValue} />
      </LabelledValue>
    );
  }

  return (
    // This feels a bit hacky to get this to display correctly due to the ml-3 on the inline editable field label and input.
    <div className="-ml-3">
      <InlineEditableField
        copyable={false}
        label={label}
        name="marks-and-numbers"
        onUpdate={handleChange}
        placeholder="Enter marks and numbers"
        value={marksAndNumbersValue}
      />
    </div>
  );
};

export default MarksAndNumbersInlineField;
